import React, { useEffect } from 'react';
import { Image, StyleProp, View, ViewStyle } from 'react-native';
import { Image as RNImage } from 'react-native';
import { getIntl } from '../../../utils/intl';
import Stack from '../Stack';
import { getSpacing } from '../../../utils/spacing';
import Icon from '../Icon';
import Text from '../Text';
import Skeleton from '../Skeleton';
import { colors } from '../../../utils/theme';

const intl = getIntl('components.baseComponents.ScaledImage', {
    errorLoadingImage: 'Error loading image',
});

export type ScaledImageOwnProps = {
    /**
     * The image to display. If null it is assumed the image url is still loading and a skeleton will be shown.
     */
    uri: string | null;
    restrict: 'width' | 'height';
    containerStyle: StyleProp<ViewStyle>;
    alt: string;
    /**
     * If the image is not loaded, a skeleton will be shown. This is the ratio of the non-restricted dimension of the image.
     */
    loaderRatio?: number;
    testID?: string;
};

export type ScaledImageProps = ScaledImageOwnProps;

type Dimensions = {
    width: number;
    height: number;
};

const ScaledImage: React.FC<ScaledImageProps> = (props) => {
    const [containerDimensions, setContainerDimensions] = React.useState<Dimensions | null>(null);
    const [imageDimensions, setImageDimensions] = React.useState<Dimensions | null>(null);
    const [hasError, setHasError] = React.useState(false);

    useEffect(() => {
        if (!props.uri) return;

        RNImage.getSize(
            props.uri,
            (width, height) => {
                setImageDimensions({ width, height });
            },
            (e) => {
                console.error(e);
                setHasError(true);
            },
        );
    }, [props.uri]);

    const computedImageDimensions = React.useMemo<Dimensions | null>(() => {
        if (!containerDimensions || !imageDimensions) return null;

        if (props.restrict === 'width') {
            return {
                width: containerDimensions.width,
                height: (containerDimensions.width * imageDimensions.height) / imageDimensions.width,
            };
        } else {
            return {
                height: containerDimensions.height,
                width: (containerDimensions.height * imageDimensions.width) / imageDimensions.height,
            };
        }
    }, [containerDimensions, imageDimensions, props.restrict]);

    const skeletonDimensions = React.useMemo<Dimensions | null>(() => {
        if (!containerDimensions) return null;

        const loaderRatio = props.loaderRatio || 1;
        const restrictedSize = props.restrict === 'width' ? containerDimensions.width : containerDimensions.height;
        const unrestrictedSize = restrictedSize * loaderRatio;

        if (props.restrict === 'width') {
            return {
                width: restrictedSize,
                height: unrestrictedSize,
            };
        } else {
            return {
                height: restrictedSize,
                width: unrestrictedSize,
            };
        }
    }, [containerDimensions, props.loaderRatio, props.restrict]);

    const renderContent = () => {
        if (computedImageDimensions) {
            const shouldScaleImage = (imageDimensions?.width || 0) > (containerDimensions?.width || 0);
            return (
                <Stack horizontal fullWidth justify="center" testID="image-centering-container">
                    <Image
                        style={{
                            width: shouldScaleImage ? computedImageDimensions.width : imageDimensions?.width,
                            height: shouldScaleImage ? computedImageDimensions.height : imageDimensions?.height,
                            backgroundColor: colors['Grey-Light'],
                        }}
                        // @ts-expect-error TS2322
                        source={{ uri: props.uri }}
                        alt={props.alt}
                        resizeMode={shouldScaleImage ? 'cover' : 'center'}
                    />
                </Stack>
            );
        } else if (hasError) {
            return (
                <Stack vertical fullHeight fullWidth justify="center" align={'center'} space={getSpacing(2)}>
                    <Icon name="AlertTriangle" size="button-icon" color="Gray-Text" />
                    <Text variant="caption-1" color="Gray-Text" alignText="center">
                        {intl('errorLoadingImage')}
                    </Text>
                </Stack>
            );
        } else if (skeletonDimensions) {
            return <Skeleton style={skeletonDimensions} />;
        } else {
            // initial mount - no dimensions yet
            return null;
        }
    };

    return (
        <View
            style={[{ backgroundColor: colors['Grey-Light'] }, props.containerStyle]}
            onLayout={(e) => {
                setContainerDimensions({ width: e.nativeEvent.layout.width, height: e.nativeEvent.layout.height });
            }}
            testID={props.testID}
        >
            {renderContent()}
        </View>
    );
};

export default React.memo(ScaledImage);
